import { Field, Formik } from 'formik';
import i18next from 'i18next';
import React, { useState, FC, useEffect, useMemo } from 'react';
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/de';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import styled from 'styled-components';
import { deAT } from 'date-fns/locale';
import { useAuthFetchFromRestAPI } from '../../util/api';

type DurationMin = 15 | 30 | 45 | 60;

type CreateAppointmentPayload = {
  start: string;
  end: string;
  duration: DurationMin;
}

type Slot = {
  start: string;
  end: string;
  displayStart: string;
  displayEnd: string;
  isBooked: boolean;
}

const ErrorDiv = styled.div`
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
`;
const RowDiv = styled.div`
max-height: 47vh;
overflow-y: auto;
overflow-x: hidden;
`;

type Props = {
  therapy: any,
  onCreateAppointment: (appointment: CreateAppointmentPayload) => Promise<void>;
};
export const CreateAppointmentForm: FC<Props> = ({ therapy, onCreateAppointment }) => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [isLoading, setIsLoading] = useState(false);
  const [displayDayPicker, setDisplayDayPicker] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedDuration, setDuration] = useState<DurationMin>(15);

  const today = useMemo(() => new Date(), []);
  const twoMonthDate = useMemo(() => moment().add(2, 'months').toDate(), []);

  const isOverlapping = (aS: string, aE: string, bS: string, bE: string) => {
    const aStart = moment.utc(aS);
    const aEnd = moment.utc(aE);
    const bStart = moment.utc(bS);
    const bEnd = moment.utc(bE);
    return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
  };

  const buildSlotsForDay = (
    date: string,
    durationMin: DurationMin,
    appointments: { startDateTime: string; endDateTime: string }[]
  ): Slot[] => {
    const startLocal = moment(`${date}T06:00`, 'YYYY-MM-DDTHH:mm', true);
    const endLocal = moment(`${date}T22:00`, 'YYYY-MM-DDTHH:mm', true);

    const result: Slot[] = [];
    const cursor = startLocal.clone();

    while (cursor.isBefore(endLocal)) {
      const start = cursor.clone().utc().toISOString();
      const end = cursor.clone().add(durationMin, 'minutes').utc().toISOString();

      if (moment.utc(end).isAfter(endLocal.clone().utc())) {
        break;
      }

      const isBooked = appointments.some(a => isOverlapping(a.startDateTime, a.endDateTime, start, end));

      result.push({
        start,
        end,
        displayStart: cursor.format('HH:mm'),
        displayEnd: cursor.clone().add(durationMin, 'minutes').format('HH:mm'),
        isBooked,
      });

      cursor.add(15, 'minutes');
    }
    return result;
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setIsLoading(true);

      try {
        const appointments = await authFetch('/api/v1/appointment/therapist');
        const list: { startDateTime: string; endDateTime: string }[] = Array.isArray(appointments) ? appointments : [];

        const dayAppointments = list.filter(a =>
          moment.utc(a.startDateTime).local().format('YYYY-MM-DD') === moment(selectedDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD'));

        const slots = buildSlotsForDay(selectedDate, selectedDuration, dayAppointments);

        if (!cancel) {
          setSlots(slots);
        }
      } catch (error) {
        console.error('Error loading appointments', error);
        if (!cancel) {
          setSlots([]);
        }
      } finally {
        if (!cancel) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [authFetch, selectedDate, selectedDuration, therapy]);

  const initialValues = {
    dateDisplay: moment(selectedDate, 'YYYY-MM-DD', true).format('DD-MM-YYYY'),
    start: '',
    end: '',
    duration: selectedDuration as number,
  };

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      onSubmit={async ({ start, end, duration }, { setSubmitting }) => {
        try {
          if (!start || !end) {
            return;
          }

          const durNumber = Number(duration) as DurationMin;
          await onCreateAppointment({ start, end, duration: durNumber });
        } finally {
          setSubmitting(false);
        }
      }}
      validate={({ dateDisplay, start, end }) => {
        const errors: { dateDisplay?: string; start?: string, end?: string } = {};

        const dateSelection = moment(dateDisplay, 'DD-MM-YYYY', true);
        if (!dateSelection.isValid()) {
          errors.dateDisplay = i18next.t('createAppointmentForm.validation.date');
        } else {
          const min = moment().startOf('day');
          const max = moment().add(2, 'months').endOf('day');

          if (dateSelection.isBefore(min)) {
            errors.dateDisplay = i18next.t('trainingPlan.validation.pastDate');
          }

          if (dateSelection.isAfter(max)) {
            errors.dateDisplay = i18next.t('trainingPlan.validation.futureDate');
          }
        }

        if (!start) {
          errors.start = i18next.t('createAppointmentForm.validation.startTime');
        }

        if (!end) {
          errors.end = i18next.t('createAppointmentForm.validation.endTime');
        }

        if (!errors.start && !errors.end) {
          const st = moment.utc(start, moment.ISO_8601, true);
          const en = moment.utc(end, moment.ISO_8601, true);

          if (!st.isValid()) {
            errors.start = i18next.t('createAppointmentForm.validation.startTime');
          }

          if (!en.isValid()) {
            errors.end = i18next.t('createAppointmentForm.validation.endTime');
          }

          if (st.isValid() && en.isValid()) {
            if (!en.isAfter(st)) {
              errors.end = i18next.t('createAppointmentForm.validation.startTimeEndTime');
            }

            if (st.isSameOrBefore(moment.utc())) {
              errors.start = i18next.t('createAppointmentForm.validation.pastTime');
            }
          }
        }

        return errors;
      }}
    >
      {({ values, errors, dirty, isValid, isSubmitting, handleSubmit, setFieldValue }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group controlId="date">
            <Form.Label column={false}>{i18next.t('createAppointmentForm.labels.date')}</Form.Label>
            <Field name="dateDisplay">
              {({ field }) => (
                <>
                  <InputGroup className="mb-3" onClick={() => setDisplayDayPicker(!displayDayPicker)}>
                    <Form.Control disabled value={field.value} />
                    <Button
                      variant="dark"
                      className="pt-1 pe-3 ps-3 pb-0 rounded-0"
                      onClick={(event) => {
                        event.stopPropagation(); setDisplayDayPicker(!displayDayPicker);
                      }}>
                      <i className="fas fa-calendar" />
                    </Button>
                  </InputGroup>
                  { displayDayPicker && (
                    <DayPicker
                      mode="single"
                      numberOfMonths={1}
                      startMonth={today}
                      endMonth={twoMonthDate}
                      disabled={[{ before: today, after: twoMonthDate }]}
                      locale={deAT}
                      onDayClick={(day) => {
                        const selected = moment(day).format('YYYY-MM-DD');
                        setSelectedDate(selected);
                        setFieldValue('dateDisplay', moment(day).format('DD-MM-YYYY'));
                        setFieldValue('start', '');
                        setFieldValue('end', '');
                        setDisplayDayPicker(false);
                      }}
                    />
                  )}
                  {errors.dateDisplay && <ErrorDiv>{errors.dateDisplay}</ErrorDiv>}
                </>
              )}
            </Field>
          </Form.Group>

          <Form.Group controlId="duration">
            <Form.Label>{i18next.t('createAppointmentForm.labels.selectDuration')}</Form.Label>
            <div className="mb-3 ms-2">
              {[15, 30, 45, 60].map((dur) => (
                <Form.Check
                  key={dur}
                  inline
                  type="radio"
                  name="durationGroup"
                  label={`${dur} ${i18next.t('createAppointmentForm.labels.min')}`}
                  checked={selectedDuration === dur}
                  onChange={() => {
                    setDuration(dur as DurationMin);
                    setFieldValue('start', '');
                    setFieldValue('end', '');
                  }}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group controlId="slots">
            <Form.Label>{i18next.t('createAppointmentForm.labels.startTime')}</Form.Label>
            <RowDiv id="create-appointment-form">
              <Row className="me-3 ms-0">
                {isLoading ? (<Col md={12}>Loading...</Col>) : (
                  <Col md={11} className="p-0">
                    {slots.length > 0 ? (
                      <Row className="nopadding">
                        {slots.map((slot, idx) => {
                          const active = values.start === slot.start;
                          return (
                            <Col md={3} xs={4} key={`slot-${idx}`} className="pe-0">
                              <Button
                                size="sm"
                                className="m-1 form-control p-0 bold-border-2"
                                disabled={slot.isBooked}
                                title={slot.isBooked
                                  ? i18next.t('createAppointmentForm.labels.bookedToolTip')
                                  : `${slot.displayStart} - ${slot.displayEnd}`}
                                variant={slot.isBooked ? 'outline-dark' : active ? 'primary' : 'outline-primary'}
                                onClick={async () => {
                                  await setFieldValue('start', slot.start);
                                  await setFieldValue('end', slot.end);
                                }}
                              >
                                {slot.displayStart}
                              </Button>
                            </Col>
                          );
                        })}
                      </Row>
                    ) : (
                      <Col md={12}>{i18next.t('createAppointmentForm.labels.noAvailableTime')}</Col>
                    )}
                  </Col>
                )}
              </Row>
            </RowDiv>
          </Form.Group>

          {values.start && values.end && (
            <Alert variant="dark">
              <span>
                {values.dateDisplay}{' '}
                {i18next.t('createAppointmentForm.labels.selectedTimeAt')}{' '}
                {moment.utc(values.start).local().format('HH:mm')} - {moment.utc(values.end).local().format('HH:mm')}
              </span>
            </Alert>
          )}

          {errors.start && <ErrorDiv>{errors.start}</ErrorDiv>}

          <Button type="submit" disabled={!dirty || !isValid || isSubmitting || !values.start || !values.end}>
            {i18next.t('createAppointmentForm.labels.submit')}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default CreateAppointmentForm;
